import { createRouter, createWebHistory } from 'vue-router';
import UnauthenticatedLayout from '@/layouts/UnauthenticatedLayout.vue';
import RoleForm from '@/pages/user/RoleForm.vue';
import { isTokenValid, hasPermission, ensureTokenParsed, rememberLoginDestination } from '@/helpers/auth';
import { VIEW_DATA_INGESTION, VIEW_CALLS, EMAIL_VALIDATION, ACTIVITY_REPORTS, APPOINTMENT_EMAILS, APPOINTMENT_CALLS } from '@/constants/permissions';

const routes = [
  {
    path: '/auth',
    component: UnauthenticatedLayout,
    children: [
      {path: 'login', name: 'login', component: () => import('@/pages/login/LoginPage')},
      {
        path: 'forgot-password',
        name: 'forgot-password',
        component: () => import('@/pages/login/ForgotPasswordPage')
      },
      {path: 'token/:token', name: 'authToken', component: () => import('@/pages/login/Auth.vue')}
    ]
  },
  {
    path: '/',
    component: () => import('@/layouts/AuthenticatedLayout.vue'),
    meta: {requiresAuth: true},
    children: [
      {path: '', name: 'dashboard', component: () => import('@/pages/dashboard/DashboardPage')},
      {
        path: 'accounts',
        name: 'accounts',
        component: () => import('@/pages/directory/Accounts.vue')
      },
      {
        path: 'account/:id',
        name: 'account',
        component: () => import('@/pages/directory/Account.vue')
      },
      {
        path: 'search',
        name: 'search',
        component: () => import('@/pages/search/SearchPage.vue'),
        meta: { permission: 'View Search' }
      },
      {
        path: 'calls',
        name: 'calls',
        component: () => import('@/pages/calls/CallsPage.vue'),
        meta: { permission: VIEW_CALLS }
      },
      {
        path: 'appointments',
        name: 'appointments',
        component: () => import('@/pages/appointments/AppointmentsPage.vue'),
        meta: { permission: APPOINTMENT_EMAILS }
      },
      {
        path: 'appointment-calls',
        name: 'appointment-calls',
        component: () => import('@/pages/appointment-calls/AppointmentCallsPage.vue'),
        meta: { permission: APPOINTMENT_CALLS }
      },
      {
        path: 'talk-track',
        name: 'talk-track',
        component: () => import('@/pages/talk-track/TalkTrack.vue')
      },
      {
        path: 'talk-track/editor',
        name: 'talk-track-editor',
        component: () => import('@/pages/talk-track/Editor.vue')
      },
      {
        path: 'talk-track/analytics',
        name: 'talk-track-analytics',
        component: () => import('@/pages/talk-track/Analytics.vue')
      },
      {
        path: 'talk-track/reports',
        name: 'talk-track-reports',
        component: () => import('@/pages/talk-track/Reports.vue')
      },
      {
        path: 'talk-track/adherence',
        name: 'talk-track-adherence',
        meta: { permission: 'Adherence Reporting' },
        component: () => import('@/pages/talk-track/Adherence.vue')
      },
      {
        path: 'sequencer',
        name: 'sequencer',
        component: () => import('@/pages/sequencer/Sequencer.vue')
      },
      {
        path: 'sequencer/activity',
        name: 'sequencer-activity',
        component: () => import('@/pages/sequencer/SequencerActivity.vue'),
        meta: { permission: ACTIVITY_REPORTS }
      },
      {
        path: 'sequencer/:id',
        name: 'sequence',
        component: () => import('@/pages/sequencer/SequencerAccount.vue')
      },
      {
        path: 'sequencer/:id/:strategy_id',
        name: 'strategy',
        component: () => import('@/pages/sequencer/SequencerAccount.vue')
      },
      {
        path: 'data-ingestion',
        name: 'data-ingestion',
        component: () => import('@/pages/data_ingestion/DataIngestionList.vue'),
        meta: { permission: VIEW_DATA_INGESTION }
      },
      {
        path: 'data-ingestion/upload/:id',
        name: 'data-ingestion-upload',
        component: () => import('@/pages/data_ingestion/UploadDetail.vue'),
        meta: { permission: VIEW_DATA_INGESTION }
      },
      {
        path: 'data-ingestion/upload/:id/validation',
        name: 'data-ingestion-validation',
        component: () => import('@/pages/data_ingestion/ValidationResults.vue'),
        meta: { permission: VIEW_DATA_INGESTION }
      },
      {
        path: 'data-export',
        name: 'data-export',
        component: () => import('@/pages/data-export/DataExportPage.vue')
      },
      {
        path: 'email-validation',
        name: 'email-validation',
        component: () => import('@/pages/email-validation/EmailValidationPage.vue'),
        meta: { permission: EMAIL_VALIDATION }
      },
      {
        path: 'data-export/industry-buckets',
        name: 'industry-buckets',
        component: () => import('@/pages/data-export/IndustryBucketsPage.vue')
      },
      {
        path: 'extension',
        name: 'extension',
        component: () => import('@/pages/extension/ExtensionPage.vue')
      },
      {
        path: 'survey-reports',
        name: 'survey-reports',
        component: () => import('@/pages/survey/SurveyReport.vue')
      },
      {
        path: 'heyreach',
        name: 'heyreach',
        component: () => import('@/pages/heyreach/HeyreachPage.vue')
      },
      {
        path: 'smartlead',
        name: 'smartlead',
        component: () => import('@/pages/smartlead/SmartleadPage.vue')
      },
      {
        path: 'documentation',
        name: 'documentation',
        component: () => import('@/pages/documentation/DocumentationPage')
      },
      {path: 'settings', name: 'settings', component: () => import('@/pages/settings/SettingsPage')},
      {
        path: 'users',
        name: 'users',
        component: () => import('@/pages/user/Users.vue')
      },
      {
        path: '/roles',
        name: 'roles',
        component: () => import('@/pages/user/Roles.vue'),
      },
      {
        path: '/roles/create',
        name: 'CreateRole',
        component: RoleForm,
      },
      {
        path: '/roles/:id',
        name: 'EditRole',
        component: RoleForm,
        props: true,
      },
      {
        path: '/users/create',
        name: 'CreateUser',
        component: () => import('@/pages/user/UserForm.vue'),
      },
      {
        path: '/users/:id',
        name: 'EditUser',
        component: () => import('@/pages/user/UserForm.vue'),
        props: true,
      },
      {
        path: '/dnc',
        name: 'dnc',
        component: () => import('@/pages/dnc/Dnc.vue')
      },
      {
        path: '/reports/ai-assistant',
        name: 'ai-assistant-reporting',
        meta: { permission: 'AI Assistant Reporting' },
        component: () => import('@/pages/reports/AiAssistant.vue')
      },
      {
        path: '/reports/demo',
        name: 'demo-reporting',
        meta: { permission: 'Demo Reporting' },
        component: () => import('@/pages/reports/DemoReport.vue')
      },
      {
        path: '/notifications',
        name: 'notifications',
        component: () => import('@/pages/notifications/Notifications.vue')
      },
      {
        path: '/smartpixl',
        name: 'smartpixl-overview',
        component: () => import('@/pages/smartpixl/SmartPixl.vue'),
      },
      // Legacy routes — redirect to unified page
      { path: '/smartpixl/dealers', name: 'smartpixl-dealers', redirect: { name: 'smartpixl-overview' } },
      { path: '/smartpixl/facebook', name: 'smartpixl-dashboard', redirect: { name: 'smartpixl-overview' } },
      { path: '/smartpixl/email', name: 'smartpixl-email-dashboard', redirect: { name: 'smartpixl-overview' } },
      { path: '/smartpixl/records', name: 'smartpixl-records', redirect: { name: 'smartpixl-overview' } },
      { path: '/smartpixl/leads/:dealerId', name: 'smartpixl-lead-records', redirect: { name: 'smartpixl-overview' } },
      { path: '/smartpixl/unsubscribes', name: 'smartpixl-unsubscribes', redirect: { name: 'smartpixl-overview' } },
      { path: '/smartpixl/unsubscribes/:dealerId', name: 'smartpixl-unsubscribes-dealer', redirect: { name: 'smartpixl-overview' } },
      { path: '/smartpixl/templates', name: 'smartpixl-templates', redirect: { name: 'smartpixl-overview' } },
      {
        path: '/smartpixl/contacts',
        name: 'smartpixl-contacts',
        component: () => import('@/pages/smartpixl/SmartPixlContacts.vue'),
      },
      {
        path: '/visitor-resolution-pixel',
        name: 'visitor-resolution-pixel',
        component: () => import('@/pages/visitor-resolution-pixel/VisitorResolutionPixel.vue'),
      },
    ]
  },
  // 404 Handler
  {
    path: '/:pathMatch(.*)*',
    component: UnauthenticatedLayout,
    children: [{path: '', name: '404', component: () => import('@/NotFound')}]
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes
});

router.beforeEach(async (to, from, next) => {
  if (to.fullPath === '/auth/login') {
    next();
  }

  const token = localStorage.getItem('aiq_token');
  if (to.meta.requiresAuth) {
    if (!token || !(await isTokenValid(token))) {
      rememberLoginDestination(to.fullPath);
      next('/auth/login');
      return;
    }
    ensureTokenParsed();
    if (to.meta.permission && !hasPermission(to.meta.permission)) {
      next({ name: 'dashboard' });
      return;
    }
    next();
  } else {
    if (token && !to.query.error && (to.name === 'login' || to.name === 'forgot-password' || to.name === 'authToken')) {
      next('/');
      return;
    }
    next();
  }
});

export default router;
